<template>
  <v-container
    fluid
    class="release-kit-page pa-0"
  >
    <v-row class="no-gutters">
      <v-col>
        <v-card class="py-9 px-12 elevation-0">
          <v-card-title class="primary--text headline">
            Release Kit
          </v-card-title>

          <v-card-subtitle class="subtitle-1">
            Stakeholder Communication Templates
          </v-card-subtitle>

          <v-card-text class="font-weight-medium">
            <v-alert
              v-if="!isInternalUser"
              outlined
              color="warning"
              class="mb-4"
            >
              The Release Kit is available only to internal users.
            </v-alert>

            <template v-else>
              <v-alert
                outlined
                color="info"
                class="mb-4"
              >
                Copy stakeholder-ready release communication for launch coordination and updates.
              </v-alert>

              <v-row>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-card
                    outlined
                    class="pa-4 communication-card"
                  >
                    <div class="d-flex justify-space-between align-center mb-3">
                      <div class="subtitle-2 font-weight-bold">
                        Stakeholder Email
                      </div>
                      <v-btn
                        outlined
                        class="text-none"
                        @click="copyReleaseCommunication('email')"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'copy']"
                          class="mr-2"
                        />
                        Copy Email
                      </v-btn>
                    </div>
                    <div class="caption coolgray--text mb-3">
                      Best for leadership, account teams, and structured release announcements.
                    </div>
                    <pre :class="['communication-copy', communicationCopyClass]">{{ stakeholderEmailText }}</pre>
                  </v-card>
                </v-col>

                <v-col
                  cols="12"
                  md="4"
                >
                  <v-card
                    outlined
                    class="pa-4 communication-card"
                  >
                    <div class="d-flex justify-space-between align-center mb-3">
                      <div class="subtitle-2 font-weight-bold">
                        Slack Summary
                      </div>
                      <v-btn
                        outlined
                        class="text-none"
                        @click="copyReleaseCommunication('slack')"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'copy']"
                          class="mr-2"
                        />
                        Copy Slack
                      </v-btn>
                    </div>
                    <div class="caption coolgray--text mb-3">
                      Best for launch channels, quick internal updates, and handoff threads.
                    </div>
                    <pre :class="['communication-copy', communicationCopyClass]">{{ stakeholderSlackText }}</pre>
                  </v-card>
                </v-col>

                <v-col
                  cols="12"
                  md="4"
                >
                  <v-card
                    outlined
                    class="pa-4 communication-card"
                  >
                    <div class="d-flex justify-space-between align-center mb-3">
                      <div class="subtitle-2 font-weight-bold">
                        Forwarding Note
                      </div>
                      <v-btn
                        outlined
                        class="text-none"
                        @click="copyReleaseCommunication('forward')"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'copy']"
                          class="mr-2"
                        />
                        Copy Note
                      </v-btn>
                    </div>
                    <div class="caption coolgray--text mb-3">
                      Best for account teams or partners who need a concise, client-safe summary.
                    </div>
                    <pre :class="['communication-copy', communicationCopyClass]">{{ stakeholderForwardText }}</pre>
                  </v-card>
                </v-col>
              </v-row>
            </template>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar
      v-model="showCopySnackbar"
      :timeout="2000"
      color="success"
    >
      {{ copySnackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import themeClasses from '@/mixins/themeClasses'

const { mapGetters } = createNamespacedHelpers('auth')

export default {
  name: 'ReleaseKit',
  mixins: [themeClasses],
  data() {
    return {
      showCopySnackbar: false,
      copySnackbarMessage: '',
    }
  },
  computed: {
    ...mapGetters([
      'isInternalUser',
    ]),

    communicationCopyClass() {
      return this.isDarkThemeContext() ? 'communication-copy-dark' : 'communication-copy-light'
    },

    stakeholderEmailText() {
      return [
        'Subject: iAdOps Q1 2026 release summary',
        '',
        'Team,',
        '',
        'We have completed the latest iAdOps Q1 2026 release cycle.',
        'This release centers on stronger Projects and Admin workflows,',
        'better onboarding support, improved authentication reliability,',
        'and safer project-data operations.',
        '',
        'Highlights:',
        '- Admin Project create flows are now more consistent across',
        '  Projects and Sheets workflows.',
        '- Firestore project operations use safer merge-write behavior',
        '  in key merge and repair paths.',
        '- The project-data audit packet and review outputs were refreshed',
        '  to support remediation planning.',
        '- Dashboard onboarding, guided tours, contextual help, and QA',
        '  checklist coverage continue to mature as product features.',
        '',
        'Release tags:',
        '- v5.7.0-prod.20260402.5f60000',
        '- v5.7.1-prod.20260402.e432cd6',
        '',
        'We also updated release documentation and internal QA summaries',
        'to make this release window easier to communicate and review.',
        '',
        'Thanks,',
        'Technology',
      ].join('\n')
    },

    stakeholderSlackText() {
      return [
        'iAdOps Q1 2026 release update',
        '',
        'The latest release cycle is complete.',
        '',
        'What changed:',
        '- Admin Project create flows now behave more consistently across Projects and Sheets.',
        '- Firestore project merge and repair paths now use safer merge-write behavior.',
        '- Project-data audit outputs and handoff docs were refreshed for remediation follow-up.',
        '- Onboarding, help, tours, and QA coverage continue to expand across the app.',
        '',
        'Latest tags:',
        '- v5.7.0-prod.20260402.5f60000',
        '- v5.7.1-prod.20260402.e432cd6',
        '',
        'Release notes and internal QA summary are updated in Docs.',
      ].join('\n')
    },

    stakeholderForwardText() {
      return [
        'Subject: iAdOps release update for client sharing',
        '',
        'Hi team,',
        '',
        'Here is a concise summary you can forward when clients or',
        'cross-functional partners ask what changed in the latest',
        'iAdOps release cycle.',
        '',
        'Highlights:',
        '- Project and admin workflows are more consistent and easier to manage.',
        '- Data handling around project maintenance is safer and more reliable.',
        '- Onboarding, help content, and release documentation are easier to use.',
        '',
        'If you need the internal launch summary or full release details,',
        'see the release notes in iAdOps Docs.',
        '',
        'Thanks,',
        'Technology',
      ].join('\n')
    },
  },
  methods: {
    async copyReleaseCommunication(type) {
      let text = this.stakeholderForwardText
      let message = 'Forward note copied to clipboard.'

      if (type === 'email') {
        text = this.stakeholderEmailText
        message = 'Stakeholder email copied to clipboard.'
      } else if (type === 'slack') {
        text = this.stakeholderSlackText
        message = 'Slack summary copied to clipboard.'
      }

      try {
        await navigator.clipboard.writeText(text)
        this.copySnackbarMessage = message
        this.showCopySnackbar = true
      } catch (error) {
        this.copySnackbarMessage = 'Could not copy release communication.'
        this.showCopySnackbar = true
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.release-kit-page {
  max-width: 1200px;
  margin: 0 auto;
}

.communication-card {
  height: 100%;
}

.communication-copy {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.55;
  margin: 0;
  padding: 12px;
  border-radius: 6px;
}

.communication-copy-light {
  color: #111827;
  background-color: rgba(0, 0, 0, 0.04);
}

.communication-copy-dark {
  color: #f3f4f6;
  background-color: rgba(255, 255, 255, 0.08);
}
</style>
